import { COL_NAMES, LATIN_LETTERS, type LatinPrompt } from '../generators/latin.ts'

/**
 * 5×5 grid with the `?` cell highlighted. Column (α–ε) and row (1–5)
 * identifiers are rendered around the grid — the official solutions and the
 * generator's explanations reference cells as γ4, β1, … (PLAN-Z M8/M11 feedback).
 * Sized in min() units so it fits a 360 px viewport without horizontal scroll.
 */
export function LatinGrid({ prompt }: { prompt: LatinPrompt }) {
  return (
    <div className="latin-grid" role="grid" aria-label="Latin square puzzle">
      <div role="row" className="latin-row">
        <span className="latin-corner" aria-hidden />
        {COL_NAMES.map((name) => (
          <span key={name} role="columnheader" className="latin-col-label">
            {name}
          </span>
        ))}
      </div>
      {prompt.cells.map((row, r) => (
        <div key={r} role="row" className="latin-row">
          <span role="rowheader" className="latin-row-label">
            {r + 1}
          </span>
          {row.map((cell, c) => {
            const isQuestion = r === prompt.question.row && c === prompt.question.col
            const cls = isQuestion
              ? 'latin-cell latin-cell-question'
              : cell !== null
                ? 'latin-cell'
                : 'latin-cell latin-cell-empty'
            return (
              <span key={c} role="gridcell" className={cls}>
                {isQuestion ? '?' : (cell ?? '')}
              </span>
            )
          })}
        </div>
      ))}
    </div>
  )
}

interface LatinAnswerProps {
  selected: string | null
  onSelect: (letter: string) => void
  disabled?: boolean
  /** Set after submit to reveal the right option. */
  correctLetter?: string | null
}

/** The five A–E options; large tap targets (≥44 px) per PLAN-Z M4. */
export function LatinAnswer({
  selected,
  onSelect,
  disabled = false,
  correctLetter = null,
}: LatinAnswerProps) {
  return (
    <div className="latin-answer" role="group" aria-label="Answer options">
      {LATIN_LETTERS.map((L) => {
        const isSelected = selected === L
        const cls = [
          'latin-option',
          isSelected ? 'selected' : '',
          correctLetter !== null && L === correctLetter ? 'correct' : '',
        ]
          .filter(Boolean)
          .join(' ')
        return (
          <button
            key={L}
            type="button"
            className={cls}
            aria-pressed={isSelected}
            disabled={disabled}
            onClick={() => onSelect(L)}
          >
            {L}
          </button>
        )
      })}
    </div>
  )
}
